import Component from '@glimmer/component';
import ProgressTrackerItems from './progress-tracker-items.gts';
import { Popover } from 'tio-ui/components/popover';
import type { Step } from './progress-tracker.gts';
import type { TOC } from '@ember/component/template-only';
import { PhCheck } from 'ember-phosphor-icons';

export interface VerticalProgressTrackerSignature {
  Args: {
    primaryColor?: string;
    secondaryColor?: string;
    actionColor?: string;
    steps: Step[];
  };
  Element: HTMLElement;
}

export interface Detail {
  componentName?: string;
  props?: Record<string, unknown>;
}

interface S {
  step: Step;
  index: number;
  inProgress?: boolean;
  details?: Detail[];
}

const isEmptyComponent = (component: object) => {
  return Object.keys(component).length === 0;
};

const hasDetails = (step: Step) => {
  return step.details && step.details.length > 0;
};

const Details: TOC<S> = <template>
  <Popover @placement="left" @offsetOptions={{38}} as |pop|>
    <button
      class="mt-2 text-center text-gray-800 cursor-pointer
        {{if @inProgress 'font-semibold text-gray-600'}}"
      data-test-progress-trigger
      type="button"
      {{pop.trigger}}
      {{pop.anchor}}
    >{{@step.title}}</button>
    {{#if (hasDetails @step)}}
      <pop.Content
        @class="bg-transparent border-0 border-gray-200 w-[26.8rem] p-8"
        data-test-progress-details
      >
        <div class="relative bg-white w-96 mb-2 p-2 border border-gray-300 rounded-lg shadow-md">

          <p class="text-sm font-semibold uppercase">{{@step.title}}</p>
          <div>
            {{#each @step.details as |detail|}}
              <ProgressTrackerItems
                @componentName={{detail.componentName}}
                @props={{detail.props}}
              />
            {{/each}}
          </div>
          <div
            class="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 rotate-45 w-4 h-4 bg-white border-r border-t border-gray-300"
          ></div>
        </div>
      </pop.Content>
    {{/if}}
  </Popover>
</template>;

export default class VerticalProgressTracker extends Component<VerticalProgressTrackerSignature> {
  plusOne(value: number) {
    return value + 1;
  }

  get primaryColor() {
    return this.args.primaryColor ? this.args.primaryColor : 'violet-800';
  }

  get secondaryColor() {
    return this.args.secondaryColor ? this.args.secondaryColor : 'violet-300';
  }

  get actionColor() {
    return this.args.actionColor ? this.args.actionColor : 'yellow-500';
  }

  get nonEmptySteps() {
    return this.args.steps.filter((step) => !isEmptyComponent(step));
  }

  isLastStep(index: number, steps: Step[]) {
    return index === steps.length - 1;
  }

  <template>
    <div ...attributes>
      <ol class="relative text-gray-500 border-s border-violet-800">
        {{#each this.nonEmptySteps as |step index|}}
          {{#if step.completed}}
            <li class="mb-12 ms-6">
              <div class="flex items-center">
                <div
                  class="absolute flex items-center justify-center w-8 h-8 bg-{{this.primaryColor}}
                    rounded-full -start-4 ring-4 ring-white"
                >
                  <PhCheck @size="24" class="text-white" />
                </div>
                <Details @step={{step}} @index={{index}} />
              </div>
            </li>
          {{else if step.inProgress}}
            <li class="mb-12 ms-6">
              <div class="flex items-center">
                <div
                  class="absolute flex items-center justify-center w-8 h-8 bg-violet-100 rounded-full border-2 border-gray-200 border-{{this.primaryColor}}
                    -start-4 ring-4 ring-white"
                >
                  <div class="h-2 w-2 rounded-full bg-{{this.primaryColor}}"></div>
                </div>
                <Details @step={{step}} @index={{index}} @inProgress={{true}} />
              </div>
            </li>
            {{! Control flow for if a step is not necessary via program template }}
          {{else if (isEmptyComponent step)}}{{else}}
            <li class="mb-12 ms-6">
              <div class="flex items-center">
                <div
                  class="absolute flex items-center justify-center w-8 h-8 bg-white rounded-full border-2 border-gray-200 border-{{this.primaryColor}}
                    -start-4 text-{{this.primaryColor}}
                    ring-4 ring-white"
                >
                  {{this.plusOne index}}
                </div>
                <Details @step={{step}} @index={{index}} />
              </div>
            </li>
          {{/if}}
        {{/each}}
      </ol>
    </div>
  </template>
}
